/**
 * Push notifications (APNs via Capacitor) — safe, additive wiring.
 *
 * Mirrors the defensive style of lib/share.js: everything is accessed through
 * `window.Capacitor.Plugins.PushNotifications`, so if the plugin isn't
 * installed yet (or we're running on the web) every call is a silent no-op and
 * the app keeps working exactly as before.
 *
 * To turn it on natively:
 *   1. cd frontend && npm i @capacitor/push-notifications
 *   2. npx cap sync ios
 *   3. Xcode → App target → Signing & Capabilities → + Push Notifications
 *      (and enable Background Modes → Remote notifications)
 *
 * Once installed, on login the app asks permission, gets an APNs device token,
 * and POSTs it to /devices/register so the backend can push to this phone.
 */
import { apiClient } from "../auth";

let _inited = false;

function pushPlugin() {
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  if (!cap || typeof cap.isNativePlatform !== "function" || !cap.isNativePlatform()) {
    return null; // web build — nothing to do
  }
  // Android push needs Firebase/FCM (google-services.json). It isn't wired up
  // yet, and calling register() there throws "Default FirebaseApp is not
  // initialized" — which crashes the whole app on launch. Restrict push to iOS
  // (APNs) until FCM is added; on Android this is a clean no-op.
  if (typeof cap.getPlatform === "function" && cap.getPlatform() !== "ios") {
    return null;
  }
  return (cap.Plugins && cap.Plugins.PushNotifications) || null;
}

/** Are we on a native build with the push plugin available? */
export function pushAvailable() {
  return !!pushPlugin();
}

/**
 * Register this device for push. Call once the user is logged in.
 * Never throws — any failure leaves the app untouched.
 */
export async function initPush() {
  try {
    const PN = pushPlugin();
    if (!PN || _inited) return;
    _inited = true;

    // Attach listeners BEFORE register() so the token event is never missed.
    try {
      PN.addListener("registration", async (t) => {
        const token = t && t.value;
        if (!token) return;
        try {
          await apiClient().post("/devices/register", { token, platform: "ios" });
        } catch (_) { /* offline / transient — backend prunes stale anyway */ }
      });
      PN.addListener("registrationError", () => { /* noop */ });
      // Tap on a notification → deep-link into the app.
      PN.addListener("pushNotificationActionPerformed", (action) => {
        try {
          const data = (action && action.notification && action.notification.data) || {};
          if (data.link) window.location.assign(data.link);
        } catch (_) { /* noop */ }
      });
    } catch (_) { /* addListener unsupported → ignore */ }

    // Ask permission (only prompts once), then register with APNs.
    let perm;
    try { perm = await PN.checkPermissions(); } catch (_) { perm = { receive: "prompt" }; }
    if (perm && (perm.receive === "prompt" || perm.receive === "prompt-with-rationale")) {
      try { perm = await PN.requestPermissions(); } catch (_) { return; }
    }
    if (!perm || perm.receive !== "granted") return;
    await PN.register();
  } catch (_) {
    // Swallow everything — push is a bonus, never a blocker.
  }
}

/** Best-effort unregister of a specific token (e.g. on logout). */
export async function unregisterPush(token) {
  try {
    if (!token) return;
    await apiClient().post("/devices/unregister", { token, platform: "ios" });
  } catch (_) { /* noop */ }
}
